/**
 * Invoice API Hooks
 * React Query hooks for invoice CRUD operations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import config from "@/lib/config";
import type {
  CreateInvoiceInput,
  UpdateInvoiceInput,
  InvoiceQueryParams,
  UpdateInvoiceStatusInput,
  InvoiceHistoryEntry,
} from "../types/invoice.types";

const INVOICE_KEYS = {
  all: ["invoices"] as const,
  lists: () => [...INVOICE_KEYS.all, "list"] as const,
  list: (params: InvoiceQueryParams) => [...INVOICE_KEYS.lists(), params] as const,
  details: () => [...INVOICE_KEYS.all, "detail"] as const,
  detail: (id: string) => [...INVOICE_KEYS.details(), id] as const,
  stats: () => [...INVOICE_KEYS.all, "stats"] as const,
  history: (id: string) => [...INVOICE_KEYS.all, "history", id] as const,
};

/**
 * Helper function for API calls with credentials
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth-data");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers as Record<string, string>,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get all invoices with pagination and filtering
 */
export function useInvoices(params: InvoiceQueryParams = {}) {
  return useQuery({
    queryKey: INVOICE_KEYS.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set("page", params.page.toString());
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.sortBy) searchParams.set("sortBy", params.sortBy);
      if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
      if (params.search) searchParams.set("search", params.search);
      if (params.startDate) searchParams.set("startDate", params.startDate);
      if (params.endDate) searchParams.set("endDate", params.endDate);
      if (params.customerId) searchParams.set("customerId", params.customerId);
      if (params.includeDeleted !== undefined) searchParams.set("includeDeleted", params.includeDeleted.toString());
      if (params.invoiceType) searchParams.set("invoiceType", params.invoiceType);

      return fetchWithAuth(`/api/invoices?${searchParams.toString()}`);
    },
  });
}

/**
 * Get invoice statistics
 */
export function useInvoiceStats() {
  return useQuery({
    queryKey: INVOICE_KEYS.stats(),
    queryFn: async () => {
      return fetchWithAuth("/api/invoices/stats");
    },
  });
}

/**
 * Get single invoice by ID
 */
export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: INVOICE_KEYS.detail(id!),
    queryFn: async () => {
      return fetchWithAuth(`/api/invoices/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create new invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvoiceInput) => {
      return fetchWithAuth("/api/invoices", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
    },
  });
}

/**
 * Update existing invoice
 */
export function useUpdateInvoice(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateInvoiceInput) => {
      return fetchWithAuth(`/api/invoices/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
    },
  });
}

/**
 * Delete invoice
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return fetchWithAuth(`/api/invoices/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
    },
  });
}

/**
 * Restore deleted invoice
 */
export function useRestoreInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return fetchWithAuth(`/api/invoices/${id}/restore`, {
        method: "POST",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
    },
  });
}

/**
 * Cancel invoice (creates cancellation invoice)
 */
export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      return fetchWithAuth(`/api/invoices/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.history(variables.id) });
    },
  });
}

/**
 * Copy invoice as new draft
 */
export function useCopyInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return fetchWithAuth(`/api/invoices/${id}/copy`, {
        method: "POST",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() });
    },
  });
}

/**
 * Hook: Update Invoice Status
 */
export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: UpdateInvoiceStatusInput["status"] }) => {
      return fetchWithAuth(`/api/invoices/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
    },
    onSuccess: async (_, variables) => {
      // Wait for all invalidations and refetches to complete
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.detail(variables.id), refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists(), refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.history(variables.id), refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats(), refetchType: 'active' }),
      ]);
    },
  });
}

/**
 * Hook: Update Invoice Dates
 */
export function useUpdateInvoiceDates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, sentAt, paidAt }: {
      id: string;
      sentAt?: string | null;
      paidAt?: string | null;
    }) => {
      const result = await fetchWithAuth(`/api/invoices/${id}/dates`, {
        method: "PATCH",
        body: JSON.stringify({ sentAt, paidAt }),
      });
      return { ...result, id }; // Return id for use in onSuccess
    },
    onSuccess: async (data) => {
      // Wait for invalidation and refetch to complete
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.detail(data.id) }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.history(data.id) }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.lists() }),
        queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.stats() }),
      ]);
    },
  });
}

/**
 * Hook: Get Invoice History
 */
export function useInvoiceHistory(id: string) {
  return useQuery<InvoiceHistoryEntry[]>({
    queryKey: INVOICE_KEYS.history(id),
    queryFn: () => fetchWithAuth(`/api/invoices/${id}/history`),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: true,
  });
}

/**
 * Function: Download Invoice PDF
 * Generates PDF on-the-fly and triggers download
 */
export async function downloadInvoicePDF(id: string, invoiceNumber: string) {
  const token = localStorage.getItem("auth-data");
  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${config.apiUrl}/api/invoices/${id}/pdf`, {
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    throw new Error("PDF konnte nicht heruntergeladen werden");
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Rechnung-${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Hook: Preview Invoice Number
 * Fetches the next invoice number that would be generated
 */
export function usePreviewInvoiceNumber(customerId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["invoice-number-preview", customerId],
    queryFn: () => {
      const params = customerId ? `?customerId=${customerId}` : '';
      return fetchWithAuth(`/api/invoices/preview-number${params}`);
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}
